import { FC, Fragment, useCallback, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import { useDidMount } from "rooks";
import { TabPanel } from "@mui/lab";
import TabContext from "@mui/lab/TabContext";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import { SavingsPlanCoverageDrawerContentHeaderInfoCards } from "./SavingsPlanCoverageDrawerContentHeaderInfoCards";
import { CommitmentsDrawerHeader } from "../../../../common/components/drawer/header/CommitmentsDrawerHeader";
import { CommitmentDrawerTabs } from "@/components/pages/commtiments/common/components/drawer/CommitmentDrawerTabs";
import { CommitmentDrawerTabType } from "@/components/pages/commtiments/common/components/drawer/utils/constants";
import { SavingsPlanCoverageDrawerCoverageTab } from "@/components/pages/commtiments/utilization-and-coverage/components/drawers/coverage-drawer/tabs/SavingsPlanCoverageDrawerCoverageTab";
import { SavingsPlanCoverageDrawerRecommendationTab } from "@/components/pages/commtiments/utilization-and-coverage/components/drawers/coverage-drawer/tabs/SavingsPlanCoverageDrawerRecommendationTab";
import { coverageRecommendationsPayloadSelector } from "@/store/commitments/selectors/coverage-and-utilization/details-drawer/recommendations/coverageRecommendationsPayloadSelector";
import { useGetUsersMeOrganisationsCurrentSavingsPlansRecommendationsQuery } from "@/services/cloudchipr.api";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { savingsPlanSelectedCoverageDataAccountNameAndIdSelector } from "@/store/commitments/selectors/coverage-and-utilization/details-drawer/resource-type/savingsPlanSelectedCoverageDataAccountNameAndIdSelector";
import { commitmentsDetailsDrawerTargetTypeSelector } from "@/store/commitments/selectors/common/details-drawer/commitmentsDetailsDrawerTargetTypeSelector";
import { getUtilizationAndCoverageDetailsThunk } from "@/store/commitments/thunks/getUtilizationAndCoverageDetailsThunk";
import { savingsPlanSelectedCoverageDataResourceTypeSelector } from "@/store/commitments/selectors/coverage-and-utilization/details-drawer/resource-type/savingsPlanSelectedCoverageDataResourceTypeSelector";
import { commitmentsResourceTypeNames } from "@/components/pages/commtiments/common/utils/constants/labels";

interface SavingsPlanCoverageDrawerContentProps {
  onClose(): void;
}

export const SavingsPlanCoverageDrawerContent: FC<
  SavingsPlanCoverageDrawerContentProps
> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState("coverage");

  const payload = useAppSelector(coverageRecommendationsPayloadSelector);

  const { data } =
    useGetUsersMeOrganisationsCurrentSavingsPlansRecommendationsQuery(payload);
  const accountId = useAppSelector(
    savingsPlanSelectedCoverageDataAccountNameAndIdSelector,
  );
  const resourceType = useAppSelector(
    savingsPlanSelectedCoverageDataResourceTypeSelector,
  );
  const targetType = useAppSelector(commitmentsDetailsDrawerTargetTypeSelector);

  const tabChangeHandler = useCallback((tab: CommitmentDrawerTabType) => {
    setTab(tab);
  }, []);

  const tabs = useMemo(() => {
    const count = data?.length ?? 0;
    return [
      {
        value: "coverage",
        label: (
          <Typography variant="body2" fontWeight="medium">
            Utilization
          </Typography>
        ),
      },
      {
        value: "recommendations",
        disabled: count === 0,
        label: (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight="medium">
              Recommendations
            </Typography>
            <Chip
              label={count}
              size="small"
              sx={{
                "& .MuiChip-label": {
                  color: "text.secondary",
                },
              }}
            />
          </Stack>
        ),
      },
    ];
  }, [data]);

  useDidMount(() => {
    dispatch(getUtilizationAndCoverageDetailsThunk());
  });

  const resourceTypeName = resourceType
    ? commitmentsResourceTypeNames[resourceType]
    : "Resource Type";

  return (
    <Fragment>
      <CommitmentsDrawerHeader
        title={targetType === "resource_type" ? resourceTypeName : accountId}
        onClose={onClose}
        label={
          targetType === "resource_type"
            ? "Resource Type Coverage:"
            : "Account Coverage:"
        }
      >
        {targetType === "resource_type" && (
          <SavingsPlanCoverageDrawerContentHeaderInfoCards />
        )}
      </CommitmentsDrawerHeader>
      <Stack bgcolor="grey.50" overflow="auto" height="100%">
        <TabContext value={tab}>
          <CommitmentDrawerTabs onTabChange={tabChangeHandler} tabs={tabs} />
          <TabPanel value="coverage" sx={{ p: 0, flex: 1 }}>
            <SavingsPlanCoverageDrawerCoverageTab />
          </TabPanel>
          <TabPanel value="recommendations" sx={{ p: 0, flex: 1 }}>
            <SavingsPlanCoverageDrawerRecommendationTab />
          </TabPanel>
        </TabContext>
      </Stack>
    </Fragment>
  );
};
